import React, { useState, useEffect, useRef } from "react";
import { Loader2, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const formatErr = (detail) => {
  if (!detail) return "Could not save phone number.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return detail.msg || String(detail);
};

/**
 * Shown as a non-dismissible modal whenever an authenticated user has no phone_normalized
 * on file (legacy Google/admin accounts created before the phone-mandatory rule). Calls
 * PATCH /auth/me/phone — once saved, user.phone_needed flips false and the modal hides.
 */
const PhoneCompletionModal = () => {
  const { user, loading, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const seeded = useRef(false);

  // hide if not logged in or already has phone
  const needed = !loading && user && user.phone_needed;

  useEffect(() => {
    if (needed && !seeded.current && user?.phone) {
      setPhone(user.phone);
      seeded.current = true;
    }
  }, [needed, user]);

  if (!needed) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.patch("/auth/me/phone", { phone }, { withCredentials: true });
      toast.success("Phone number saved.");
      if (refresh) await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur flex items-center justify-center px-4" data-testid="phone-completion-modal">
      <div className="w-full max-w-md bg-spike-elevated border border-spike-blue/30 rounded-2xl p-7">
        <div className="inline-flex items-center gap-2 text-xs font-orbitron font-bold text-spike-blue-light tracking-[0.3em] uppercase">
          <Phone className="w-3.5 h-3.5"/> Complete your profile
        </div>
        <h2 className="mt-2 font-orbitron text-2xl font-black uppercase text-spike-text">
          One more <span className="text-spike-blue-light">step.</span>
        </h2>
        <p className="mt-3 text-sm text-spike-text-muted font-exo leading-relaxed">
          We need a phone number on file so the front-desk staff can reach you about your booking
          or check-in. This is a one-time step.
        </p>

        {err && <p data-testid="phone-modal-error" className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-3">{err}</p>}

        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="phone-modal-form">
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim"/>
            <input
              type="tel"
              data-testid="phone-modal-input"
              required
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 647-555-0100"
              className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="phone-modal-submit"
            className="w-full inline-flex justify-center items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-5 py-3 rounded-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save & Continue"}
          </button>
        </form>
        <p className="mt-4 text-[0.65rem] text-spike-dim text-center font-exo">10–15 digits. Stored securely; never shared.</p>
      </div>
    </div>
  );
};

export default PhoneCompletionModal;
